import React, { useState, useCallback, useEffect } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from '@/components/ui-compat'
import { useMutation } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faDownload, faFilePdf, faTimes } from '@fortawesome/free-solid-svg-icons'
import { toastr } from 'react-redux-toastr'

import useJobProgress from '../../../hooks/useJobProgress'
import JobProgressIndicator from '../../../components/JobProgress/JobProgressIndicator'
import * as Mutation from '../../../api/gql/mutation'

interface SnagFilter {
  projectId?: string
  buildingId?: string
  floorId?: string
  status?: string[]
  priority?: string[]
  infrastructureType?: string[]
  assignedToId?: string
  search?: string
  snagIds?: string[]
}

interface PdfExportModalProps {
  isOpen: boolean
  toggle: () => void
  projectId: string
  filter?: SnagFilter
  snagIds?: string[]
  selectedCount?: number
}

const PdfExportModal: React.FC<PdfExportModalProps> = ({
  isOpen,
  toggle,
  projectId,
  filter,
  snagIds,
  selectedCount,
}) => {
  const { t, i18n } = useTranslation()
  const [started, setStarted] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)

  const handleComplete = useCallback((output: any) => {
    // Download URL will be fetched from job.pdfUrl
  }, [])

  const handleError = useCallback((error: string) => {
    toastr.error(t('Snags_export_error'), error)
  }, [t])

  const { job, loading, startPolling, stopPolling } = useJobProgress({
    pollInterval: 2000,
    maxPollDuration: 10 * 60 * 1000, // 10 minutes for large exports
    onComplete: handleComplete,
    onError: handleError,
  })

  const [exportSnagsPdf] = useMutation(Mutation.EXPORT_SNAGS_PDF, {
    onCompleted: (data) => {
      const jobId = data?.exportSnagsPdf?.id
      if (jobId) {
        setStarted(true)
        startPolling(jobId)
      }
    },
    onError: (error) => {
      toastr.error(t('Snags_export_error'), error.message)
    },
  })

  // When job completes, set the download URL
  useEffect(() => {
    if (job?.status === 'COMPLETED' && job?.pdfUrl) {
      setDownloadUrl(job.pdfUrl)
    }
  }, [job?.status, job?.pdfUrl])

  const handleStartExport = () => {
    // Build filter for mutation
    const graphqlFilter: any = {}
    if (filter?.buildingId) graphqlFilter.buildingId = filter.buildingId
    if (filter?.floorId) graphqlFilter.floorId = filter.floorId
    if (filter?.status?.length) graphqlFilter.status = filter.status
    if (filter?.priority?.length) graphqlFilter.priority = filter.priority
    if (filter?.infrastructureType?.length) graphqlFilter.infrastructureType = filter.infrastructureType
    if (filter?.assignedToId) graphqlFilter.assignedToId = filter.assignedToId
    if (filter?.search) graphqlFilter.search = filter.search
    // If snagIds provided, only export those specific snags
    if (snagIds?.length) graphqlFilter.snagIds = snagIds

    exportSnagsPdf({
      variables: {
        projectId,
        filter: Object.keys(graphqlFilter).length > 0 ? graphqlFilter : null,
        language: i18n.language || 'en',
      },
    })
  }

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank')
    }
  }

  const handleClose = () => {
    stopPolling()
    setStarted(false)
    setDownloadUrl(null)
    toggle()
  }

  const isProcessing = started && job && ['PENDING', 'PROCESSING'].includes(job.status)
  const isCompleted = job?.status === 'COMPLETED'
  const isFailed = job?.status === 'FAILED' || job?.status === 'CANCELLED'

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md" centered backdrop="static">
      <ModalHeader toggle={handleClose}>
        <Icon icon={faFilePdf as any} className="mr-2 text-destructive" />
        {t('Snags_export_pdf_title', 'Export to PDF')}
      </ModalHeader>

      <ModalBody>
        {!started ? (
          <div className="text-center py-3">
            <Icon
              icon={faFilePdf as any}
              className="text-destructive mb-3"
              style={{ fontSize: '3rem' }}
            />
            <p className="text-muted-foreground mb-0">
              {selectedCount && selectedCount > 0
                ? t('Snags_export_pdf_selected_description', 'Generate a PDF report of {{count}} selected snags.', { count: selectedCount })
                : t('Snags_export_pdf_description', 'Generate a PDF report of all snags based on your current filters.')
              }
            </p>
          </div>
        ) : (
          <div className="py-3">
            <JobProgressIndicator
              job={job}
              loading={loading}
              showSteps={true}
              showPercentage={true}
              onRetry={handleStartExport}
            />

            {isCompleted && downloadUrl && (
              <div className="text-center mt-4">
                <p className="text-[hsl(var(--success))] font-bold mb-3">
                  {t('Snags_export_pdf_complete', 'PDF ready for download')}
                </p>
                {job?.pageCount && (
                  <p className="text-muted-foreground small mb-2">
                    {t('Snags_export_pdf_page_count', '{{count}} pages', { count: job.pageCount })}
                    {job.fileSize && (
                      <> - {(job.fileSize / 1024 / 1024).toFixed(1)} MB</>
                    )}
                  </p>
                )}
                <Button color="success" size="lg" onClick={handleDownload}>
                  <Icon icon={faDownload as any} className="mr-2" />
                  {t('Snags_export_pdf_download', 'Download PDF')}
                </Button>
              </div>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        {!started ? (
          <>
            <Button color="secondary" onClick={handleClose}>
              {t('Common_cancel', 'Cancel')}
            </Button>
            <Button color="primary" onClick={handleStartExport}>
              <Icon icon={faFilePdf as any} className="mr-2" />
              {t('Snags_export_pdf_start', 'Generate PDF')}
            </Button>
          </>
        ) : isProcessing ? (
          <Button color="secondary" onClick={handleClose}>
            <Icon icon={faTimes as any} className="mr-1" />
            {t('Common_close', 'Close')}
          </Button>
        ) : (
          <Button color="secondary" onClick={handleClose}>
            {t('Common_close', 'Close')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  )
}

export default PdfExportModal
